import {
  DimeButton,
  DimeDialog,
  DimeDialogContent,
  DimeDialogDescription,
  DimeDialogFooter,
  DimeDialogHeader,
  DimeDialogTitle,
} from '@nudool/design-system';
import * as React from 'react';

/** Replaces `window.confirm` for the large-export gate, which enterprise browser policy can silently no-op to `true`. */
export function useExportConfirmDialog(): {
  confirmLargeExport: (message: string) => Promise<boolean>;
  confirmDialog: React.ReactNode;
} {
  const [message, setMessage] = React.useState<string | null>(null);
  const resolveRef = React.useRef<((confirmed: boolean) => void) | null>(null);

  // An unmount mid-prompt declines, or the awaiting export sits upstream of its abort signal and never settles.
  React.useEffect(() => {
    return () => {
      resolveRef.current?.(false);
      resolveRef.current = null;
    };
  }, []);

  function confirmLargeExport(nextMessage: string): Promise<boolean> {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setMessage(nextMessage);
    });
  }

  function respond(confirmed: boolean) {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setMessage(null);
  }

  const confirmDialog = (
    <DimeDialog
      open={message !== null}
      onOpenChange={(next) => {
        if (!next) respond(false);
      }}
    >
      <DimeDialogContent showCloseButton={false}>
        <DimeDialogHeader>
          <DimeDialogTitle>Large export</DimeDialogTitle>
          <DimeDialogDescription>{message}</DimeDialogDescription>
        </DimeDialogHeader>
        <DimeDialogFooter>
          <DimeButton type="button" variant="outline" size="sm" onClick={() => respond(false)}>
            Cancel
          </DimeButton>
          <DimeButton type="button" size="sm" onClick={() => respond(true)}>
            Export anyway
          </DimeButton>
        </DimeDialogFooter>
      </DimeDialogContent>
    </DimeDialog>
  );

  return { confirmLargeExport, confirmDialog };
}
